/**
 * LiveKitClient - Core LiveKit Room Management
 *
 * Handles LiveKit room connections for audio consultation:
 * - Room connection and disconnection
 * - Audio track publishing
 * - Text stream handling for CDSS agent communication (primary channel)
 * - Data packet fallback support
 * - Error handling and automatic reconnection
 * - Connection quality monitoring
 *
 * Architecture:
 * Frontend (LiveKitClient) → LiveKit Room (consult.pranik.ai) →
 * LiveKit Agent (avatar.pranik.ai) → Process Audio → Send CDSS Data via Text Stream
 *
 * Communication:
 * - Primary: Text Streams (lk.transcription) - Real-time CDSS data from agent
 * - Fallback: Data Packets - Backward compatibility for structured messages
 * - Outgoing: Control messages sent via reliable data channel
 */

import {
  Room,
  RoomEvent,
  Track,
  LocalAudioTrack,
  RemoteParticipant,
  ConnectionQuality,
  RoomOptions,
  DisconnectReason,
  DataPacket_Kind,
} from 'livekit-client';
import { createComponentLogger } from '@/lib/utils/logger';
import type { ClinicalAssessmentEntry, PrescribedMedicine } from '@/ayurveda/types/ayurveda';

const logger = createComponentLogger('LiveKitClient');

/**
 * Connection state types
 */
export type LiveKitConnectionState =
  'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'disconnecting';

/**
 * Connection quality types
 */
export type LiveKitConnectionQuality = 'excellent' | 'good' | 'poor' | 'unknown';

/**
 * Transcription message from agent
 */
export interface TranscriptionMessage {
  text: string;
  is_partial: boolean;
  speaker?: 'patient' | 'doctor';
  timestamp: number;
}

/**
 * CDSS data from agent
 */
export interface CDSSData {
  possible_diagnoses?: string[];
  confirmed_diagnoses?: string[];
  follow_up_questions?: string[];
  additional_suggestions?: string[];
  suggested_medications?: string[];
  prescribed_medications?: string[];
  current_medications?: string[];
  symptoms?: string[];
  allergies?: string[];
  ordered_investigations?: string[];
  diet_recommendations?: string[];
  lifestyle_recommendations?: string[];
  viruddha_ahaara?: string[];
  family_history?: string[];
  known_diseases?: string[];
  commonly_eaten_food?: string[];
  suggestive_slokas?: Array<{
    title: string;
    source: string;
    sanskrit: string;
    meaning: string;
    clinical_interpretation: string;
  }>;
  suggestive_slokas_update?: boolean;
  /** Astha Sthaana Pareeksha / Prakruthi/Dosha/Aama / Diagnosis / Treatment
   *  chip values — full-replace array each turn (backend already resolves
   *  the sticky pa_soap/manual lock server-side). */
  clinical_assessment?: ClinicalAssessmentEntry[];
  clinical_assessment_update?: boolean;
  /** Structured medicines table (Medicine Name/Form/Dosage/Anupana/Timing/
   *  Frequency/Duration/Additional Info), full-replace each turn — same
   *  convention as `clinical_assessment`. Falls back to the plain-text
   *  `treatment_medicines` ClinicalAssessmentEntry when not sent. */
  prescribed_medicines?: PrescribedMedicine[];
  /** Live location → weather + regional-diet delta (Environmental Profile
   *  panel), published once a debounced background lookup resolves after the
   *  patient states their city. Snake_case keys match the backend's
   *  DesaInformation/RegionalDiet shape directly — no camelCase conversion
   *  at this layer, that happens in parseAyurveda.ts. */
  desa_information?: {
    city?: string;
    state?: string;
    season?: string;
    desa_type?: string;
    temperature?: number;
    humidity?: number;
    staple_diet?: string;
    regional_diet?: {
      grains?: string;
      pulses?: string;
      fruits?: string;
      veg?: string;
      dairy?: string;
      spice_level?: number;
    };
  };
  desa_information_update?: boolean;
  /** Live Personal History delta, published directly on change — drives
   *  PatientInformationCard's Personal History fields (including the
   *  addiction icon highlighting). No `diet` key — that's sourced
   *  separately. */
  personal_history?: {
    appetite?: string;
    sleep?: string;
    exercise?: string;
    bowel?: string;
    bladder?: string;
    addiction?: string;
    menstrual?: string;
  };
  personal_history_update?: boolean;
  /** Live "Patient's Reported Information" delta (chief complaint, duration,
   *  mode of onset, and the History section fields), published directly on
   *  change — drives PatientInformationCard in a live-only (no kiosk)
   *  session. Nested under its own key (not the top-level `family_history`
   *  above, which is a separate, pre-existing cumulative-list mechanism). */
  history_information?: {
    chief_complaint?: string;
    duration?: string;
    mode_of_onset?: string;
    history?: string;
    past_illness?: string;
    treatment_history?: string;
    family_history?: string;
    family_social_occupational?: string;
    current_medication?: string;
  };
  history_information_update?: boolean;
}

function hasCDSSPayload(data: unknown): data is CDSSData {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const payload = data as Record<string, unknown>;
  const cdssKeys: Array<keyof CDSSData> = [
    'possible_diagnoses',
    'confirmed_diagnoses',
    'follow_up_questions',
    'additional_suggestions',
    'suggested_medications',
    'prescribed_medications',
    'current_medications',
    'symptoms',
    'allergies',
    'ordered_investigations',
    'diet_recommendations',
    'lifestyle_recommendations',
    'viruddha_ahaara',
    'family_history',
    'known_diseases',
    'commonly_eaten_food',
    'suggestive_slokas',
    'suggestive_slokas_update',
    'clinical_assessment',
    'clinical_assessment_update',
    'prescribed_medicines',
    'desa_information',
    'desa_information_update',
    'personal_history',
    'personal_history_update',
    'history_information',
    'history_information_update',
  ];

  return cdssKeys.some((key) => key in payload);
}

/**
 * Analysis complete message from agent
 */
export interface AnalysisCompleteMessage {
  session_id: string;
  result: Record<string, unknown>; // AIAnalysisResult
  chunks_processed: number;
  analysis_time: number;
}

/**
 * Session info response from agent (via session_info topic)
 */
export interface SessionInfoResponse {
  success: boolean;
  summary: Record<string, unknown>; // SessionSummary
  summary_file?: string;
  message_id?: string; // Correlation ID for tracking
}

/**
 * Session end acknowledgement from agent
 */
export interface SessionEndAck {
  type: 'session_end_ack';
  message_id: string;
  audio_chunks_received?: number;
  processing_started: boolean;
  timestamp: number;
}

/**
 * Processing status update from agent
 */
export interface ProcessingStatus {
  type: 'processing_status';
  message_id: string;
  status: 'transcribing' | 'analyzing' | 'generating_summary' | 'complete' | 'error';
  progress_percent?: number;
  error_message?: string;
  timestamp: number;
}

/**
 * Data packet message types from agent (FALLBACK - for backward compatibility)
 * Note: Text streams (lk.transcription) are the primary communication channel
 */
export interface DataPacketMessage {
  type: 'transcription' | 'cdss' | 'analysis_complete' | 'error' | 'ready';
  data:
    TranscriptionMessage | CDSSData | AnalysisCompleteMessage | { message: string; code?: string };
}

/**
 * Event handler types
 */
export type EventHandler<T = unknown> = (data: T) => void;

export interface LiveKitEventHandlers {
  onTranscription?: EventHandler<TranscriptionMessage>;
  onCDSS?: EventHandler<CDSSData>;
  onAnalysisComplete?: EventHandler<AnalysisCompleteMessage>;
  onSessionSummary?: EventHandler<SessionInfoResponse>;
  onSessionEndAck?: EventHandler<SessionEndAck>;
  onProcessingStatus?: EventHandler<ProcessingStatus>;
  onError?: EventHandler<{ message: string; code?: string }>;
  onReady?: EventHandler<void>;
  onConnectionStateChange?: EventHandler<LiveKitConnectionState>;
  onConnectionQualityChange?: EventHandler<LiveKitConnectionQuality>;
}

/**
 * LiveKitClient configuration
 */
export interface LiveKitClientConfig {
  /**
   * Audio constraints for microphone
   */
  audioConstraints?: MediaTrackConstraints;

  /**
   * Automatic reconnection enabled
   */
  autoReconnect?: boolean;

  /**
   * Maximum reconnection attempts
   */
  maxReconnectAttempts?: number;

  /**
   * Reconnection delay in milliseconds
   */
  reconnectDelay?: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<LiveKitClientConfig> = {
  audioConstraints: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 16000, // 16kHz for speech recognition
    channelCount: 1, // Mono
  },
  autoReconnect: true,
  maxReconnectAttempts: 5,
  reconnectDelay: 1000, // 1 second
};

/**
 * LiveKitClient - Main class for LiveKit room management
 */
export class LiveKitClient {
  private room: Room | null = null;
  private localAudioTrack: LocalAudioTrack | null = null;
  private eventHandlers: Map<string, EventHandler[]> = new Map();
  private config: Required<LiveKitClientConfig>;
  private connectionState: LiveKitConnectionState = 'disconnected';
  private connectionQuality: LiveKitConnectionQuality = 'unknown';
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private serverUrl = '';
  private token = '';

  // Local audio recording for playback
  private localMediaRecorder: MediaRecorder | null = null;
  private localAudioChunks: Blob[] = [];
  private localAudioStream: MediaStream | null = null;
  // Flag to track if we're in the process of submitting (waiting for summary)
  private isSubmitting = false;
  // Flag to track if session summary has been received (prevents any further actions)
  private hasSummaryReceived = false;
  // Legacy flag for backward compatibility - maps to hasSummaryReceived
  private hasSubmitted = false;

  // Message tracking for correlation and acknowledgements
  private currentMessageId: string | null = null;
  private audioChunksSent = 0;
  private lastSessionEndAck: SessionEndAck | null = null;
  private recordingStartTime: number | null = null;

  constructor(handlers: LiveKitEventHandlers = {}, config: LiveKitClientConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.setupEventHandlers(handlers);
  }

  /**
   * Setup event handlers from constructor
   */
  private setupEventHandlers(handlers: LiveKitEventHandlers): void {
    if (handlers.onTranscription) {
      this.on('transcription', handlers.onTranscription);
    }
    if (handlers.onCDSS) {
      this.on('cdss', handlers.onCDSS);
    }
    if (handlers.onAnalysisComplete) {
      this.on('analysis_complete', handlers.onAnalysisComplete);
    }
    if (handlers.onSessionSummary) {
      this.on('session_summary', handlers.onSessionSummary);
    }
    if (handlers.onSessionEndAck) {
      this.on('session_end_ack', handlers.onSessionEndAck);
    }
    if (handlers.onProcessingStatus) {
      this.on('processing_status', handlers.onProcessingStatus);
    }
    if (handlers.onError) {
      this.on('error', handlers.onError);
    }
    if (handlers.onReady) {
      this.on('ready', handlers.onReady);
    }
    if (handlers.onConnectionStateChange) {
      this.on('connection_state_change', handlers.onConnectionStateChange);
    }
    if (handlers.onConnectionQualityChange) {
      this.on('connection_quality_change', handlers.onConnectionQualityChange);
    }
  }

  /**
   * Connect to LiveKit room
   */
  async connect(token: string, serverUrl: string): Promise<void> {
    if (this.room && this.connectionState === 'connected') {
      logger.warn('Already connected to room');
      return;
    }

    this.token = token;
    this.serverUrl = serverUrl;
    this.setConnectionState('connecting');

    try {
      // Create room with optimized settings for audio consultation
      const roomOptions: RoomOptions = {
        adaptiveStream: true, // Adaptive bitrate
        dynacast: true, // Dynamic broadcast
        // Audio-specific optimizations
        audioCaptureDefaults: this.config.audioConstraints,
        // Reconnection settings
        reconnectPolicy: {
          nextRetryDelayInMs: (context) => {
            // Exponential backoff: 1s, 2s, 4s
            // Stop retrying after maxReconnectAttempts
            if (context.retryCount >= this.config.maxReconnectAttempts) {
              return null;
            }
            const delay = Math.min(
              this.config.reconnectDelay * Math.pow(2, context.retryCount),
              10000
            );
            logger.log(`Retry ${context.retryCount + 1} after ${delay}ms`);
            return delay;
          },
        },
      };

      this.room = new Room(roomOptions);

      // Setup room event listeners
      this.setupRoomListeners();

      // Connect to room
      logger.log('Connecting to LiveKit room:', serverUrl);
      await this.room.connect(serverUrl, token);

      logger.log('Successfully connected to LiveKit room');
      this.setConnectionState('connected');
      this.reconnectAttempts = 0;
    } catch (error) {
      logger.error('Failed to connect to LiveKit room:', error);
      this.setConnectionState('disconnected');
      this.emit('error', {
        message: error instanceof Error ? error.message : 'Connection failed',
        code: 'CONNECTION_FAILED',
      });

      // Attempt reconnection if enabled
      if (this.config.autoReconnect) {
        this.attemptReconnect();
      }

      throw error;
    }
  }

  /**
   * Setup room event listeners
   */
  private setupRoomListeners(): void {
    if (!this.room) return;

    // Register text stream handlers for CDSS agent (primary communication channel)
    this.setupTextStreamHandlers();

    // Data packet fallback (for backward compatibility)
    this.room.on(RoomEvent.DataReceived, this.handleDataReceived.bind(this));
    logger.log('DataReceived event listener registered');

    // Connection state changes
    this.room.on(RoomEvent.Connected, () => {
      logger.log('Room connected event');
      this.setConnectionState('connected');
    });

    this.room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      logger.log('Room disconnected:', reason);
      this.setConnectionState('disconnected');
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      logger.log('Room reconnecting...');
      this.setConnectionState('reconnecting');
    });

    this.room.on(RoomEvent.Reconnected, () => {
      logger.log('Room reconnected successfully');
      this.setConnectionState('connected');
      this.reconnectAttempts = 0;
    });

    // Connection quality changes
    this.room.on(RoomEvent.ConnectionQualityChanged, (quality: ConnectionQuality, participant) => {
      // Only track local participant quality
      if (participant === this.room?.localParticipant) {
        const qualityStr = this.mapConnectionQuality(quality);
        logger.log('Connection quality changed:', qualityStr);
        this.setConnectionQuality(qualityStr);
      }
    });

    // Track events
    this.room.on(RoomEvent.TrackPublished, (track) => {
      logger.log('Track published:', track.kind);
    });

    this.room.on(RoomEvent.TrackUnpublished, (track) => {
      logger.log('Track unpublished:', track.kind);
    });

    // Error events
    this.room.on(RoomEvent.MediaDevicesError, (error: Error) => {
      logger.error('Media devices error:', error);
      this.emit('error', {
        message: error.message,
        code: 'MEDIA_DEVICES_ERROR',
      });
    });
  }

  /**
   * Setup text stream handlers for CDSS agent communication
   * This is the PRIMARY channel for receiving CDSS data from the agent
   */
  private setupTextStreamHandlers(): void {
    if (!this.room) return;

    // Register handler for lk.transcription stream (primary CDSS channel)
    this.room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
      try {
        logger.log('Text stream received from:', participantInfo.identity);
        const message = await reader.readAll();

        // Check if it's from the CDSS agent
        if (participantInfo.identity.includes('agent')) {
          try {
            const data = JSON.parse(message);
            logger.log('CDSS Text Stream Data:', data);

            // Emit as CDSS data.
            // Some Ayurveda updates carry only `suggestive_slokas` /
            // `suggestive_slokas_update`, so don't gate on symptoms/diagnoses only.
            if (hasCDSSPayload(data)) {
              this.emit('cdss', {
                symptoms: data.symptoms,
                possible_diagnoses: data.possible_diagnoses,
                confirmed_diagnoses: data.confirmed_diagnoses,
                follow_up_questions: data.follow_up_questions,
                additional_suggestions: data.additional_suggestions,
                suggested_medications: data.suggested_medications,
                prescribed_medications: data.prescribed_medications,
                current_medications: data.current_medications,
                allergies: data.allergies,
                ordered_investigations: data.ordered_investigations,
                diet_recommendations: data.diet_recommendations,
                lifestyle_recommendations: data.lifestyle_recommendations,
                viruddha_ahaara: data.viruddha_ahaara,
                family_history: data.family_history,
                known_diseases: data.known_diseases,
                commonly_eaten_food: data.commonly_eaten_food,
                suggestive_slokas: data.suggestive_slokas,
                suggestive_slokas_update: data.suggestive_slokas_update,
                clinical_assessment: data.clinical_assessment,
                clinical_assessment_update: data.clinical_assessment_update,
                prescribed_medicines: data.prescribed_medicines,
                desa_information: data.desa_information,
                desa_information_update: data.desa_information_update,
                personal_history: data.personal_history,
                personal_history_update: data.personal_history_update,
                history_information: data.history_information,
                history_information_update: data.history_information_update,
              } as CDSSData);
            }

            // Also emit as transcription if it has text
            if (data.text) {
              this.emit('transcription', {
                text: data.text,
                is_partial: data.is_partial || false,
                speaker: data.speaker,
                timestamp: data.timestamp || Date.now(),
              } as TranscriptionMessage);
            }
          } catch (parseError) {
            logger.error('Failed to parse text stream JSON:', parseError);
            // If not JSON, treat as plain transcription
            this.emit('transcription', {
              text: message,
              is_partial: false,
              timestamp: Date.now(),
            } as TranscriptionMessage);
          }
        }
      } catch (error) {
        logger.error('Error handling text stream:', error);
        this.emit('error', {
          message: error instanceof Error ? error.message : 'Text stream error',
          code: 'TEXT_STREAM_ERROR',
        });
      }
    });

    // Register handler for session_info stream (session summary from agent)
    this.room.registerTextStreamHandler('session_info', async (reader, participantInfo) => {
      try {
        logger.log('Session info stream received from:', participantInfo.identity);
        const message = await reader.readAll();

        try {
          const data = JSON.parse(message);
          logger.log('Session Info Data (text stream):', data);

          // Check for session_started acknowledgement
          if (data.data === 'session_started') {
            logger.log('Session started acknowledgement received (via text stream)');
            this.emit('ready', undefined);
            return;
          }

          // Check for session_end acknowledgement (new)
          if (data.type === 'session_end_ack') {
            logger.log('Session end acknowledgement received (via text stream)', {
              message_id: data.message_id,
              audio_chunks_received: data.audio_chunks_received,
              processing_started: data.processing_started,
            });
            this.lastSessionEndAck = data as SessionEndAck;
            this.emit('session_end_ack', data as SessionEndAck);
            return;
          }

          // Check for processing status update (new)
          if (data.type === 'processing_status') {
            logger.log('Processing status update received (via text stream)', {
              message_id: data.message_id,
              status: data.status,
              progress_percent: data.progress_percent,
            });
            this.emit('processing_status', data as ProcessingStatus);
            return;
          }

          // Sloka-only delta update from the Ayurveda grounding worker.
          // This arrives on the `session_info` topic after `session_end`, not on
          // the regular CDSS stream, so forward it explicitly to the CDSS handler.
          if (data.suggestive_slokas_update && Array.isArray(data.suggestive_slokas)) {
            const timestamp = new Date().toISOString();
            const payloadSizeBytes =
              typeof TextEncoder !== 'undefined'
                ? new TextEncoder().encode(message).length
                : message.length;
            logger.log('Suggestive slokas delta update received (via text stream)', {
              timestamp,
              count: data.suggestive_slokas.length,
              payloadSizeBytes,
              participant: participantInfo.identity,
            });
            if (data.suggestive_slokas.length > 0) {
              logger.log(`[SlokaDebug] [${timestamp}] First sloka received via text stream`, {
                title: data.suggestive_slokas[0]?.title,
                source: data.suggestive_slokas[0]?.source,
              });
            }
            this.emit('cdss', data as CDSSData);
            return;
          }

          // Clinical-assessment delta update (Astha Sthaana Pareeksha/Prakruthi/
          // Dosha/Aama/Diagnosis/Treatment) — arrives on `session_info` after a
          // manual chip override re-broadcast, same pattern as the sloka delta above.
          if (data.clinical_assessment_update && Array.isArray(data.clinical_assessment)) {
            logger.log('Clinical assessment delta update received (via text stream)', {
              count: data.clinical_assessment.length,
              participant: participantInfo.identity,
            });
            this.emit('cdss', {
              clinical_assessment: data.clinical_assessment,
              clinical_assessment_update: true,
            } as CDSSData);
            return;
          }

          // Desa-information delta update (location → weather + regional diet,
          // Environmental Profile panel) — arrives on `session_info` once the
          // debounced background lookup resolves after the patient states
          // their city, same pattern as the sloka/clinical-assessment deltas above.
          if (data.desa_information_update && data.desa_information) {
            logger.log('Desa information delta update received (via text stream)', {
              city: data.desa_information.city,
              participant: participantInfo.identity,
            });
            this.emit('cdss', {
              desa_information: data.desa_information,
              desa_information_update: true,
            } as CDSSData);
            return;
          }

          // Personal-history delta update (addiction icon highlighting) —
          // same pattern as the desa-information delta above.
          if (data.personal_history_update && data.personal_history) {
            logger.log('Personal history delta update received (via text stream)', {
              addiction: data.personal_history.addiction,
              participant: participantInfo.identity,
            });
            this.emit('cdss', {
              personal_history: data.personal_history,
              personal_history_update: true,
            } as CDSSData);
            return;
          }

          // History-information delta update (chief complaint, duration, mode
          // of onset, and the History section fields) — same pattern as the
          // personal-history delta above. Drives PatientInformationCard in a
          // live-only (no kiosk) session.
          if (data.history_information_update && data.history_information) {
            logger.log('History information delta update received (via text stream)', {
              chief_complaint: data.history_information.chief_complaint,
              participant: participantInfo.identity,
            });
            this.emit('cdss', {
              history_information: data.history_information,
              history_information_update: true,
            } as CDSSData);
            return;
          }

          // Check if this is a session summary response (has success field)
          if (data.success !== undefined && data.summary) {
            logger.log('Session summary received via text stream (wrapped format)');

            // Parse summary if it's a string
            let summaryData = data.summary;
            if (typeof data.summary === 'string') {
              try {
                summaryData = JSON.parse(data.summary);
                logger.log('Parsed summary string to object');
              } catch (parseError) {
                logger.error('Failed to parse summary string:', parseError);
              }
            }

            // Mark summary as received - this stops reconnection attempts
            this.hasSummaryReceived = true;
            this.isSubmitting = false;

            // Send acknowledgement to backend that we received the summary
            this.sendSummaryAck(true).catch((err) => {
              logger.warn('Failed to send summary ack:', err);
            });

            this.emit('session_summary', {
              success: data.success,
              summary: summaryData,
              summary_file: data.summary_file,
              message_id: this.currentMessageId,
            } as SessionInfoResponse);
            return;
          }

          // Backend sends the summary directly (direct format - not wrapped)
          // Check if this looks like a session summary (has expected fields)
          if (data.chief_complaint || data.patient_summary || data.medications) {
            logger.log('Session summary received via text stream (direct format)');

            // Mark summary as received - this stops reconnection attempts
            this.hasSummaryReceived = true;
            this.isSubmitting = false;

            // Send acknowledgement to backend that we received the summary
            this.sendSummaryAck(true).catch((err) => {
              logger.warn('Failed to send summary ack:', err);
            });

            this.emit('session_summary', {
              success: true,
              summary: data,
              summary_file: data.summary_file,
              message_id: this.currentMessageId,
            } as SessionInfoResponse);
            return;
          }
        } catch (parseError) {
          logger.error('Failed to parse session_info JSON:', parseError);
          this.emit('error', {
            message: 'Failed to parse session info message',
            code: 'SESSION_INFO_PARSE_ERROR',
          });
        }
      } catch (error) {
        logger.error('Error handling session_info stream:', error);
        this.emit('error', {
          message: error instanceof Error ? error.message : 'Session info stream error',
          code: 'SESSION_INFO_STREAM_ERROR',
        });
      }
    });

    logger.log('Text stream handlers registered');
  }

  /**
   * Handle data received from agent via data channel (FALLBACK)
   * Note: Text streams (lk.transcription) are the primary communication channel
   * This exists for backward compatibility and testing
   */
  private handleDataReceived(
    payload: Uint8Array,
    participant?: RemoteParticipant,
    kind?: DataPacket_Kind,
    topic?: string
  ): void {
    try {
      // Decode data packet
      const decoder = new TextDecoder();
      const messageStr = decoder.decode(payload);

      logger.log('Data received from agent:', {
        topic,
        kind,
        participant: participant?.identity,
        messageStr,
        payloadLength: payload.length,
      });

      // Handle session_info topic (acknowledgements and summary from backend)
      if (topic === 'session_info') {
        try {
          const data = JSON.parse(messageStr);
          logger.log('Session Info Data received:', data);

          // Check for session_started acknowledgement
          if (data.data === 'session_started') {
            logger.log('Session started acknowledgement received');
            this.emit('ready', undefined);
            return;
          }

          // Check for session_end acknowledgement (new)
          if (data.type === 'session_end_ack') {
            logger.log('Session end acknowledgement received (via data packet)', {
              message_id: data.message_id,
              audio_chunks_received: data.audio_chunks_received,
              processing_started: data.processing_started,
            });
            this.lastSessionEndAck = data as SessionEndAck;
            this.emit('session_end_ack', data as SessionEndAck);
            return;
          }

          // Check for processing status update (new)
          if (data.type === 'processing_status') {
            logger.log('Processing status update received (via data packet)', {
              message_id: data.message_id,
              status: data.status,
              progress_percent: data.progress_percent,
            });
            this.emit('processing_status', data as ProcessingStatus);
            return;
          }

          // Sloka-only delta update from the Ayurveda grounding worker.
          // Forward this `session_info` payload into the standard CDSS callback
          // so the Consultation page can update the Reference modal in place.
          if (data.suggestive_slokas_update && Array.isArray(data.suggestive_slokas)) {
            const timestamp = new Date().toISOString();
            logger.log('Suggestive slokas delta update received (via data packet)', {
              timestamp,
              count: data.suggestive_slokas.length,
              payloadSizeBytes: payload.length,
              participant: participant?.identity,
            });
            if (data.suggestive_slokas.length > 0) {
              logger.log(`[SlokaDebug] [${timestamp}] First sloka received via data packet`, {
                title: data.suggestive_slokas[0]?.title,
                source: data.suggestive_slokas[0]?.source,
              });
            }
            this.emit('cdss', data as CDSSData);
            return;
          }

          // Clinical-assessment delta update — see the text-stream handler's
          // parallel branch above for full context.
          if (data.clinical_assessment_update && Array.isArray(data.clinical_assessment)) {
            logger.log('Clinical assessment delta update received (via data packet)', {
              count: data.clinical_assessment.length,
              participant: participant?.identity,
            });
            this.emit('cdss', {
              clinical_assessment: data.clinical_assessment,
              clinical_assessment_update: true,
            } as CDSSData);
            return;
          }

          // Desa-information delta update — see the text-stream handler's
          // parallel branch above for full context.
          if (data.desa_information_update && data.desa_information) {
            logger.log('Desa information delta update received (via data packet)', {
              city: data.desa_information.city,
              participant: participant?.identity,
            });
            this.emit('cdss', {
              desa_information: data.desa_information,
              desa_information_update: true,
            } as CDSSData);
            return;
          }

          // Personal-history delta update (addiction icon highlighting) —
          // same pattern as the desa-information delta above.
          if (data.personal_history_update && data.personal_history) {
            logger.log('Personal history delta update received (via data packet)', {
              addiction: data.personal_history.addiction,
              participant: participant?.identity,
            });
            this.emit('cdss', {
              personal_history: data.personal_history,
              personal_history_update: true,
            } as CDSSData);
            return;
          }

          // History-information delta update (chief complaint, duration, mode
          // of onset, and the History section fields) — same pattern as the
          // personal-history delta above. Drives PatientInformationCard in a
          // live-only (no kiosk) session.
          if (data.history_information_update && data.history_information) {
            logger.log('History information delta update received (via data packet)', {
              chief_complaint: data.history_information.chief_complaint,
              participant: participant?.identity,
            });
            this.emit('cdss', {
              history_information: data.history_information,
              history_information_update: true,
            } as CDSSData);
            return;
          }

          // Check if this is a session summary response (has success field)
          if (data.success !== undefined && data.summary) {
            logger.log('Session summary received via data packet (wrapped format)');

            // Parse summary if it's a string
            let summaryData = data.summary;
            if (typeof data.summary === 'string') {
              try {
                summaryData = JSON.parse(data.summary);
                logger.log('Parsed summary string to object');
              } catch (parseError) {
                logger.error('Failed to parse summary string:', parseError);
              }
            }

            // Mark summary as received - this stops reconnection attempts
            this.hasSummaryReceived = true;
            this.isSubmitting = false;

            // Send acknowledgement to backend that we received the summary
            this.sendSummaryAck(true).catch((err) => {
              logger.warn('Failed to send summary ack:', err);
            });

            this.emit('session_summary', {
              success: data.success,
              summary: summaryData,
              summary_file: data.summary_file,
              message_id: this.currentMessageId,
            } as SessionInfoResponse);
            return;
          }

          // Check if this is a session summary (direct format with expected fields)
          if (data.chief_complaint || data.patient_summary || data.medications) {
            logger.log('Session summary received via data packet (direct format)');

            // Mark summary as received - this stops reconnection attempts
            this.hasSummaryReceived = true;
            this.isSubmitting = false;

            // Send acknowledgement to backend that we received the summary
            this.sendSummaryAck(true).catch((err) => {
              logger.warn('Failed to send summary ack:', err);
            });

            this.emit('session_summary', {
              success: true,
              summary: data,
              summary_file: data.summary_file,
              message_id: this.currentMessageId,
            } as SessionInfoResponse);
            return;
          }

          // Unknown session_info message
          logger.warn('Unknown session_info message:', data);
          return;
        } catch (parseError) {
          logger.error('Failed to parse session_info data:', parseError);
          this.emit('error', {
            message: 'Failed to parse session info message',
            code: 'SESSION_INFO_PARSE_ERROR',
          });
          return;
        }
      }

      // Handle regular data packets (legacy format)
      const message: DataPacketMessage = JSON.parse(messageStr);
      logger.log('Data packet message type:', message.type);

      // Emit event based on message type
      switch (message.type) {
        case 'transcription':
          this.emit('transcription', message.data as TranscriptionMessage);
          break;

        case 'cdss':
          this.emit('cdss', message.data as CDSSData);
          break;

        case 'analysis_complete':
          this.emit('analysis_complete', message.data as AnalysisCompleteMessage);
          break;

        case 'ready':
          this.emit('ready', undefined);
          break;

        case 'error':
          this.emit('error', message.data as { message: string; code?: string });
          break;

        default:
          logger.warn('Unknown message type:', message.type);
      }
    } catch (error) {
      logger.error('Failed to parse data packet:', error);
      this.emit('error', {
        message: 'Failed to parse agent message',
        code: 'PARSE_ERROR',
      });
    }
  }

  /**
   * Start publishing audio from microphone
   */
  async startAudioPublishing(): Promise<void> {
    if (!this.room) {
      throw new Error('Not connected to room. Call connect() first.');
    }

    if (this.localAudioTrack) {
      logger.warn('Audio track already publishing');
      return;
    }

    try {
      logger.log('Creating audio track with constraints:', this.config.audioConstraints);

      // Create local audio track
      const tracks = await this.room.localParticipant.createTracks({
        audio: this.config.audioConstraints,
      });

      const audioTrack = tracks.find((t) => t.kind === Track.Kind.Audio);

      if (!audioTrack) {
        throw new Error('Failed to create audio track');
      }

      // Publish audio track to room
      logger.log('Publishing audio track to room...');
      await this.room.localParticipant.publishTrack(audioTrack);

      this.localAudioTrack = audioTrack as LocalAudioTrack;

      // Track recording start time and reset chunk counter
      this.recordingStartTime = Date.now();
      this.audioChunksSent = 0;

      logger.log('Audio track published successfully', {
        recordingStartTime: this.recordingStartTime,
      });

      // Start local audio recording for playback
      await this.startLocalRecording();
    } catch (error) {
      logger.error('Failed to publish audio track:', error);
      this.emit('error', {
        message: error instanceof Error ? error.message : 'Failed to publish audio',
        code: 'AUDIO_PUBLISH_FAILED',
      });
      throw error;
    }
  }

  /**
   * Start local audio recording for playback
   * Records the microphone audio locally while streaming to LiveKit
   */
  private async startLocalRecording(): Promise<void> {
    try {
      // Get microphone stream for local recording
      this.localAudioStream = await navigator.mediaDevices.getUserMedia({
        audio: this.config.audioConstraints,
      });

      // Clear previous chunks
      this.localAudioChunks = [];

      // Create MediaRecorder for local recording
      this.localMediaRecorder = new MediaRecorder(this.localAudioStream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      this.localMediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.localAudioChunks.push(event.data);
          // Track audio chunks for correlation with backend
          this.audioChunksSent++;
        }
      };

      this.localMediaRecorder.onerror = (error) => {
        logger.error('Local MediaRecorder error:', error);
      };

      // Start recording with 1 second intervals
      this.localMediaRecorder.start(1000);
      logger.log('Local audio recording started for playback');
    } catch (error) {
      logger.error('Failed to start local recording:', error);
      // Don't throw - local recording is optional, LiveKit streaming is primary
    }
  }

  /**
   * Stop local audio recording and return the audio blob
   */
  private stopLocalRecording(): Blob | null {
    if (!this.localMediaRecorder) {
      return null;
    }

    try {
      // Stop MediaRecorder
      if (this.localMediaRecorder.state !== 'inactive') {
        this.localMediaRecorder.stop();
      }

      // Stop all tracks in the stream
      if (this.localAudioStream) {
        this.localAudioStream.getTracks().forEach((track) => track.stop());
        this.localAudioStream = null;
      }

      // Create blob from chunks
      if (this.localAudioChunks.length > 0) {
        const audioBlob = new Blob(this.localAudioChunks, { type: 'audio/webm;codecs=opus' });
        logger.log('Local audio recording stopped', {
          size: audioBlob.size,
          chunks: this.localAudioChunks.length,
        });
        this.localAudioChunks = [];
        this.localMediaRecorder = null;
        return audioBlob;
      }

      this.localMediaRecorder = null;
      return null;
    } catch (error) {
      logger.error('Failed to stop local recording:', error);
      return null;
    }
  }

  /**
   * Get recorded audio blob (for playback/persistence)
   */
  getRecordedAudioBlob(): Blob | null {
    if (this.localAudioChunks.length === 0) {
      return null;
    }
    return new Blob(this.localAudioChunks, { type: 'audio/webm;codecs=opus' });
  }

  /**
   * Stop publishing audio
   */
  async stopAudioPublishing(): Promise<Blob | null> {
    // Stop local recording first and get the blob
    const recordedBlob = this.stopLocalRecording();

    if (!this.localAudioTrack || !this.room) {
      logger.warn('No audio track to stop');
      return recordedBlob;
    }

    try {
      logger.log('Stopping audio track...');

      // Unpublish track
      await this.room.localParticipant.unpublishTrack(this.localAudioTrack);

      // Stop track
      this.localAudioTrack.stop();
      this.localAudioTrack = null;

      logger.log('Audio track stopped successfully');
      return recordedBlob;
    } catch (error) {
      logger.error('Failed to stop audio track:', error);
      this.emit('error', {
        message: error instanceof Error ? error.message : 'Failed to stop audio',
        code: 'AUDIO_STOP_FAILED',
      });
      return recordedBlob;
    }
  }

  /**
   * Mute the audio track (stops sending audio data while keeping connection)
   * Used for pause functionality - audio is not sent to backend during mute
   */
  async muteAudio(): Promise<void> {
    if (!this.localAudioTrack) {
      logger.warn('No audio track to mute');
      return;
    }

    try {
      logger.log('Muting audio track...');
      await this.localAudioTrack.mute();
      logger.log('Audio track muted - no audio will be sent to backend');
    } catch (error) {
      logger.error('Failed to mute audio track:', error);
      this.emit('error', {
        message: error instanceof Error ? error.message : 'Failed to mute audio',
        code: 'AUDIO_MUTE_FAILED',
      });
      throw error;
    }
  }

  /**
   * Unmute the audio track (resumes sending audio data)
   * Used for resume functionality after pause
   */
  async unmuteAudio(): Promise<void> {
    if (!this.localAudioTrack) {
      logger.warn('No audio track to unmute');
      return;
    }

    try {
      logger.log('Unmuting audio track...');
      await this.localAudioTrack.unmute();
      logger.log('Audio track unmuted - audio is now being sent to backend');
    } catch (error) {
      logger.error('Failed to unmute audio track:', error);
      this.emit('error', {
        message: error instanceof Error ? error.message : 'Failed to unmute audio',
        code: 'AUDIO_UNMUTE_FAILED',
      });
      throw error;
    }
  }

  /**
   * Check if audio is currently muted
   */
  isAudioMuted(): boolean {
    return this.localAudioTrack?.isMuted ?? false;
  }

  /**
   * Send control message to agent via data channel
   */
  async sendControlMessage(message: Record<string, unknown>): Promise<void> {
    if (!this.room) {
      throw new Error('Not connected to room');
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(message));

      logger.log('Sending control message to agent:', message);

      await this.room.localParticipant.publishData(data, {
        reliable: true,
      });
    } catch (error) {
      logger.error('Failed to send control message:', error);
      this.emit('error', {
        message: error instanceof Error ? error.message : 'Failed to send control message',
        code: 'SEND_MESSAGE_FAILED',
      });
      throw error;
    }
  }

  /**
   * Send session end signal to request final summary
   * Sends {data: "session_end"} on the session_info topic
   * This is robust and handles already-closed connections gracefully
   *
   * IMPORTANT: isSubmitting=true allows reconnection to continue
   * so we can receive the summary. hasSummaryReceived is only set
   * when the summary actually arrives.
   */
  async sendSessionEnd(): Promise<void> {
    // Prevent multiple submissions once summary is received
    if (this.hasSummaryReceived) {
      logger.warn('Session summary already received, ignoring sendSessionEnd call');
      return;
    }

    // If already submitting, this is a retry - allow it
    if (this.isSubmitting) {
      logger.log('Already in submission flow - this is a retry attempt');
    }

    // Mark as submitting - this allows reconnection to continue
    this.isSubmitting = true;
    this.hasSubmitted = true; // Legacy flag for backward compatibility

    // Check if room is still connected
    if (!this.room) {
      logger.warn('Room not connected, skipping session end signal (will disconnect anyway)');
      // Keep isSubmitting=true so reconnection works
      return;
    }

    // Check connection state - if already disconnected, just return
    if (this.connectionState === 'disconnected' || this.connectionState === 'disconnecting') {
      logger.warn('Connection already closed, skipping session end signal');
      // Keep isSubmitting=true so reconnection works
      return;
    }

    try {
      // Generate unique message ID for correlation and tracking
      this.currentMessageId = crypto.randomUUID();

      // Calculate recording duration if available
      const recordingDuration = this.recordingStartTime
        ? Math.floor((Date.now() - this.recordingStartTime) / 1000)
        : 0;

      const message = {
        data: 'session_end',
        message_id: this.currentMessageId,
        audio_chunks_count: this.audioChunksSent,
        recording_duration_seconds: recordingDuration,
        timestamp: Date.now(),
      };

      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(message));

      logger.log('Sending session end signal to agent via session_info topic', {
        message_id: this.currentMessageId,
        audio_chunks_count: this.audioChunksSent,
        recording_duration_seconds: recordingDuration,
      });

      // Send via data channel with topic identifier
      await this.room.localParticipant.publishData(data, {
        reliable: true,
        topic: 'session_info',
      });

      logger.log('Session end signal sent successfully');
    } catch (error) {
      // Handle "PC manager is closed" error gracefully - this is expected if room disconnected
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('PC manager is closed') || errorMessage.includes('not connected')) {
        logger.warn(
          'Connection already closed when sending session end - this is expected during shutdown'
        );
        // Don't emit error or throw - this is a graceful shutdown scenario
        // Keep isSubmitting=true so if we reconnect, we can still receive summary
      } else {
        logger.error('Failed to send session end signal:', error);
        // Only emit error for unexpected failures
        this.emit('error', {
          message: errorMessage,
          code: 'SESSION_END_FAILED',
        });
      }
    }
    // Note: We do NOT set isSubmitting=false here anymore
    // It stays true until summary is received, allowing reconnection
  }

  /**
   * Send quick-end signal for "Quick Submit" — Sends {data: "session_quick_end"}
   * on the session_info topic.
   *
   * Unlike sendSessionEnd, the agent sends nothing back on this path (no ACK,
   * no processing_status, no summary) because it assumes the frontend is
   * leaving the room right away. It writes the summary directly to Medplum
   * as a DiagnosticReport instead, so callers should navigate away
   * immediately after this resolves rather than waiting for any event.
   */
  async sendSessionQuickEnd(): Promise<void> {
    if (!this.room) {
      logger.warn('Room not connected, skipping quick end signal (leaving anyway)');
      return;
    }

    if (this.connectionState === 'disconnected' || this.connectionState === 'disconnecting') {
      logger.warn('Connection already closed, skipping quick end signal');
      return;
    }

    this.hasSubmitted = true;

    try {
      const messageId = crypto.randomUUID();
      const message = {
        data: 'session_quick_end',
        message_id: messageId,
      };

      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(message));

      logger.log('Sending session quick-end signal to agent via session_info topic', {
        message_id: messageId,
      });

      await this.room.localParticipant.publishData(data, {
        reliable: true,
        topic: 'session_info',
      });

      logger.log('Session quick-end signal sent successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('PC manager is closed') || errorMessage.includes('not connected')) {
        logger.warn(
          'Connection already closed when sending quick end - expected, frontend is leaving anyway'
        );
      } else {
        // Don't emit an 'error' event here — the doctor is already navigating
        // away, so surfacing this would show a stale error on a page they've
        // already left. Just log it for diagnostics.
        logger.error('Failed to send session quick-end signal:', error);
      }
    }
  }

  /**
   * Send acknowledgement to backend that summary was received
   * This lets the backend know the frontend successfully received and processed the summary
   */
  async sendSummaryAck(success: boolean = true): Promise<void> {
    // Only send if we have a room connection
    if (!this.room) {
      logger.warn('Cannot send summary ack - no room connection');
      return;
    }

    // Only send if connection is active
    if (this.connectionState === 'disconnected' || this.connectionState === 'disconnecting') {
      logger.warn('Cannot send summary ack - connection closed');
      return;
    }

    try {
      const message = {
        type: 'summary_received_ack',
        message_id: this.currentMessageId,
        success: success,
        received_at: Date.now(),
        timestamp: Date.now(),
      };

      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(message));

      logger.log('Sending summary received acknowledgement to backend', {
        message_id: this.currentMessageId,
        success,
      });

      // Send via data channel with topic identifier
      await this.room.localParticipant.publishData(data, {
        reliable: true,
        topic: 'session_info',
      });

      logger.log('Summary acknowledgement sent successfully');
    } catch (error) {
      // Don't throw - this is best-effort acknowledgement
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.warn('Failed to send summary acknowledgement:', errorMessage);
      // Connection may already be closed, which is fine
    }
  }

  /**
   * Send a manual clinical-assessment chip override to the CDSS agent
   * (Astha Sthaana Pareeksha / Prakruthi / Dosha / Aama / Diagnosis / Treatment).
   * The backend locks the value as "manual" (same precedence as "pa_soap" —
   * never overwritten by later voice extraction) and re-publishes the full
   * `clinical_assessment` array on the `session_info` topic, which this
   * client's session_info handlers already forward into the `cdss` event.
   * Rejects if the override could not be published.
   */
  async sendClinicalAssessmentOverride(parameter: string, value: string): Promise<void> {
    if (!this.room) {
      logger.warn('Cannot send clinical assessment override - no room connection');
      throw new Error('Not connected to the consultation room');
    }

    if (this.connectionState === 'disconnected' || this.connectionState === 'disconnecting') {
      logger.warn('Cannot send clinical assessment override - connection closed');
      throw new Error('Consultation connection is closed');
    }

    try {
      const message = { parameter, value, timestamp: Date.now() };
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(message));

      logger.log('Sending clinical assessment override to backend', { parameter, value });

      await this.room.localParticipant.publishData(data, {
        reliable: true,
        topic: 'clinical-assessment-override',
      });

      logger.log('Clinical assessment override sent successfully');
    } catch (error) {
      // Rethrow: the caller applies the chip optimistically and must roll it back.
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.warn('Failed to send clinical assessment override:', errorMessage);
      throw error;
    }
  }

  /**
   * Get the current message ID for correlation
   */
  getCurrentMessageId(): string | null {
    return this.currentMessageId;
  }

  /**
   * Get the count of audio chunks sent
   */
  getAudioChunksSent(): number {
    return this.audioChunksSent;
  }

  /**
   * Get the last session end acknowledgement received
   */
  getLastSessionEndAck(): SessionEndAck | null {
    return this.lastSessionEndAck;
  }

  /**
   * Check if session has been submitted (legacy - for backward compatibility)
   */
  getHasSubmitted(): boolean {
    return this.hasSubmitted;
  }

  /**
   * Check if session summary has been received
   */
  getHasSummaryReceived(): boolean {
    return this.hasSummaryReceived;
  }

  /**
   * Check if currently in submission flow (waiting for summary)
   */
  getIsSubmitting(): boolean {
    return this.isSubmitting;
  }

  /**
   * Reset submission state to allow retry
   * Call this when submission fails and user wants to retry
   * Does NOT reset hasSummaryReceived - that's permanent for the session
   */
  resetSubmissionState(): void {
    logger.log('Resetting submission state for retry');
    this.hasSubmitted = false;
    this.isSubmitting = false;
    this.currentMessageId = null;
    this.lastSessionEndAck = null;
    // Note: We do NOT reset hasSummaryReceived - if we got the summary, we're done
  }

  /**
   * Full reset for new consultation session
   */
  resetForNewSession(): void {
    logger.log('Resetting for new session');
    this.hasSubmitted = false;
    this.isSubmitting = false;
    this.hasSummaryReceived = false;
    this.reconnectAttempts = 0;
    this.currentMessageId = null;
    this.audioChunksSent = 0;
    this.lastSessionEndAck = null;
    this.recordingStartTime = null;
  }

  /**
   * Disconnect from room
   */
  disconnect(): void {
    logger.log('Disconnecting from LiveKit room...');

    // Clear reconnect timer - no more reconnection attempts
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Stop local MediaRecorder and release the raw getUserMedia stream so the
    // browser no longer shows the microphone as in-use after navigating away
    this.stopLocalRecording();

    // Stop audio track safely
    if (this.localAudioTrack) {
      try {
        this.localAudioTrack.stop();
      } catch (error) {
        logger.warn('Error stopping audio track during disconnect:', error);
      }
      this.localAudioTrack = null;
    }

    // Disconnect room safely
    if (this.room) {
      this.setConnectionState('disconnecting');
      try {
        this.room.disconnect();
      } catch (error) {
        logger.warn('Error disconnecting room:', error);
      }
      this.room = null;
    }

    this.setConnectionState('disconnected');
    logger.log('Disconnected from LiveKit room');
  }

  /**
   * Attempt reconnection with exponential backoff
   * IMPORTANT: We allow reconnection during submission (isSubmitting=true) because
   * we need the connection to receive the summary. We only block reconnection
   * after the summary has been received (hasSummaryReceived=true).
   */
  private attemptReconnect(): void {
    // Only block reconnection AFTER summary is received - connection no longer needed
    if (this.hasSummaryReceived) {
      logger.log('Skipping reconnection - summary already received');
      return;
    }

    // Allow reconnection during submission - we need it to receive the summary!
    if (this.isSubmitting) {
      logger.log('Reconnecting during submission flow - needed to receive summary');
    }

    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      logger.error('Max reconnection attempts reached');
      this.emit('error', {
        message: 'Failed to reconnect after maximum attempts',
        code: 'MAX_RECONNECT_ATTEMPTS',
      });
      return;
    }

    const delay = this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    logger.log(
      `Attempting reconnection ${this.reconnectAttempts}/${this.config.maxReconnectAttempts} in ${delay}ms`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect(this.token, this.serverUrl).catch((error) => {
        logger.error('Reconnection failed:', error);
        // Will retry again if within max attempts
      });
    }, delay);
  }

  /**
   * Register event handler
   */
  on<T = unknown>(event: string, handler: EventHandler<T>): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler as EventHandler);
  }

  /**
   * Unregister event handler
   */
  off<T = unknown>(event: string, handler: EventHandler<T>): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler as EventHandler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to all registered handlers
   */
  private emit<T = unknown>(event: string, data: T): void {
    const handlers = this.eventHandlers.get(event) || [];
    handlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        logger.error(`Error in event handler for ${event}:`, error);
      }
    });
  }

  /**
   * Set connection state and emit event
   */
  private setConnectionState(state: LiveKitConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.emit('connection_state_change', state);
    }
  }

  /**
   * Set connection quality and emit event
   */
  private setConnectionQuality(quality: LiveKitConnectionQuality): void {
    if (this.connectionQuality !== quality) {
      this.connectionQuality = quality;
      this.emit('connection_quality_change', quality);
    }
  }

  /**
   * Map LiveKit ConnectionQuality to our type
   */
  private mapConnectionQuality(quality: ConnectionQuality): LiveKitConnectionQuality {
    switch (quality) {
      case ConnectionQuality.Excellent:
        return 'excellent';
      case ConnectionQuality.Good:
        return 'good';
      case ConnectionQuality.Poor:
        return 'poor';
      default:
        return 'unknown';
    }
  }

  /**
   * Get current connection state
   */
  getConnectionState(): LiveKitConnectionState {
    return this.connectionState;
  }

  /**
   * Get current connection quality
   */
  getConnectionQuality(): LiveKitConnectionQuality {
    return this.connectionQuality;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  /**
   * Check if audio is publishing
   */
  isPublishing(): boolean {
    return this.localAudioTrack !== null;
  }

  /**
   * Get room instance (for advanced usage)
   */
  getRoom(): Room | null {
    return this.room;
  }
}
